/**
 * Diff two DOM snapshots into a ranked list of changed elements.
 *
 * Pure: no filesystem, no Playwright, no browser. Everything the diff needs is
 * in the two snapshots, which is what makes the interesting parts — path
 * matching, moved-versus-replaced, noise suppression — directly testable.
 */

import type { DomChange, DomChangeKind, DomStyleChange } from '@testrelic/core';
import type { DomNode, DomSnapshot } from './dom-capture.js';

/** Sub-pixel jitter is not a layout change; browsers round differently run to run. */
const RECT_TOLERANCE_PX = 1;

/** Beyond this the list stops being readable; the remainder is reported as a count. */
export const DEFAULT_MAX_CHANGES = 50;

export interface DomDiffOptions {
  readonly maxChanges?: number;
  /** Turn off individual categories for pages where they are pure noise. */
  readonly includeText?: boolean;
  readonly includeStyles?: boolean;
}

export interface DomDiffResult {
  readonly changes: DomChange[];
  /** Total found, which may exceed `changes.length`. */
  readonly total: number;
  /** Of the total, how many actually changed rather than merely shifted. */
  readonly rootCauses: number;
}

/** Movement alone is something that happened TO an element, not something it did. */
const MOVEMENT_ONLY: ReadonlySet<DomChangeKind> = new Set<DomChangeKind>(['moved', 'resized']);

/**
 * Did this element change, or was it just pushed around?
 *
 * Anything restyled, retexted, added or removed changed on its own account.
 * An element that only moved or resized did not — in a layout, one element
 * growing by 24px relocates everything after it, and reporting all of those as
 * peers of the edit that caused them buries the actual finding.
 */
function isRootCause(kinds: readonly DomChangeKind[]): boolean {
  return kinds.some((kind) => !MOVEMENT_ONLY.has(kind));
}

/** `div.card.accent` — what the report shows, built from what the node carries. */
function labelOf(node: DomNode): string {
  let label = node.t;
  if (node.i) label += '#' + node.i;
  if (node.c) {
    const classes = node.c.split(' ').filter(Boolean).slice(0, 3);
    if (classes.length > 0) label += '.' + classes.join('.');
  }
  return label;
}

function movedOrResized(a: DomNode, b: DomNode): DomChangeKind[] {
  const kinds: DomChangeKind[] = [];
  const dx = Math.abs(a.r[0] - b.r[0]);
  const dy = Math.abs(a.r[1] - b.r[1]);
  const dw = Math.abs(a.r[2] - b.r[2]);
  const dh = Math.abs(a.r[3] - b.r[3]);
  if (dx > RECT_TOLERANCE_PX || dy > RECT_TOLERANCE_PX) kinds.push('moved');
  if (dw > RECT_TOLERANCE_PX || dh > RECT_TOLERANCE_PX) kinds.push('resized');
  return kinds;
}

function diffStyles(a: DomNode, b: DomNode): DomStyleChange[] {
  const from = a.s ?? {};
  const to = b.s ?? {};
  const props = new Set<string>([...Object.keys(from), ...Object.keys(to)]);
  const changes: DomStyleChange[] = [];
  for (const prop of props) {
    const before = from[prop];
    const after = to[prop];
    if (before === after) continue;
    // A property present on one side and absent on the other is at its initial
    // value there — say so rather than printing an empty string.
    changes.push({
      prop,
      from: before ?? '(initial)',
      to: after ?? '(initial)',
    });
  }
  changes.sort((x, y) => x.prop.localeCompare(y.prop));
  return changes;
}

/**
 * Secondary match key, for elements whose path changed.
 *
 * An element that moved in the tree would otherwise read as one removal plus
 * one addition, which is both wrong and twice as noisy. An id is unique enough
 * to match on alone; failing that, tag plus class list is a reasonable guess
 * and a wrong guess only downgrades a moved element back to removed + added.
 */
function identityKey(node: DomNode): string | null {
  // The tag is part of the key for BOTH forms. Without it, a node whose tag
  // changed — deliberately left unmatched by the path pass so it reports as
  // removed + added rather than as an edit — was re-paired here on its id
  // alone, and a <div id="hero"> that became a <section id="hero"> came back
  // as a restyle. The class key already included the tag; the id key did not.
  if (node.i) return 'i:' + node.t + '#' + node.i;
  if (node.c) return 'c:' + node.t + '.' + node.c;
  return null;
}

interface Pairing {
  readonly pairs: Array<{ before: DomNode; after: DomNode }>;
  readonly removed: DomNode[];
  readonly added: DomNode[];
}

/** Match nodes across the two snapshots: first by path, then by identity. */
function pairNodes(baseline: readonly DomNode[], actual: readonly DomNode[]): Pairing {
  const byPath = new Map<string, DomNode>();
  for (const node of baseline) byPath.set(node.p, node);

  const pairs: Array<{ before: DomNode; after: DomNode }> = [];
  const unmatchedActual: DomNode[] = [];
  const matchedBaseline = new Set<DomNode>();

  for (const node of actual) {
    const before = byPath.get(node.p);
    // Same position but a different tag is a replacement, not an edit: a
    // <div> that became a <section> is not the same element wearing new
    // clothes, and reporting it as one would hide the swap.
    if (before && before.t !== node.t) {
      unmatchedActual.push(node);
      continue;
    }
    if (before && !matchedBaseline.has(before)) {
      matchedBaseline.add(before);
      pairs.push({ before, after: node });
    } else {
      unmatchedActual.push(node);
    }
  }

  const unmatchedBaseline = baseline.filter((node) => !matchedBaseline.has(node));

  // Second pass over what is left, by identity rather than position.
  const byIdentity = new Map<string, DomNode[]>();
  for (const node of unmatchedBaseline) {
    const key = identityKey(node);
    if (!key) continue;
    const bucket = byIdentity.get(key);
    if (bucket) bucket.push(node);
    else byIdentity.set(key, [node]);
  }

  const added: DomNode[] = [];
  const claimed = new Set<DomNode>();
  for (const node of unmatchedActual) {
    const key = identityKey(node);
    const bucket = key ? byIdentity.get(key) : undefined;
    const before = bucket?.find((candidate) => !claimed.has(candidate));
    if (before) {
      claimed.add(before);
      pairs.push({ before, after: node });
    } else {
      added.push(node);
    }
  }

  const removed = unmatchedBaseline.filter((node) => !claimed.has(node));
  return { pairs, removed, added };
}

/**
 * Identity attributes that differ.
 *
 * A class list changing from `card` to `banner` is usually the actual edit,
 * and every style difference underneath it is a consequence. Without this the
 * report shows the consequences and stays silent about the cause.
 */
function diffAttrs(a: DomNode, b: DomNode): DomStyleChange[] {
  const changes: DomStyleChange[] = [];
  if ((a.i ?? '') !== (b.i ?? '')) {
    changes.push({ prop: 'id', from: a.i ?? '(none)', to: b.i ?? '(none)' });
  }
  if ((a.c ?? '') !== (b.c ?? '')) {
    changes.push({ prop: 'class', from: a.c ?? '(none)', to: b.c ?? '(none)' });
  }
  return changes;
}

/** Share of the captured region an element covers, clamped to 0–1. */
function areaShare(node: DomNode, captureBox: readonly number[]): number {
  const total = (captureBox[2] ?? 0) * (captureBox[3] ?? 0);
  if (total <= 0) return 0;
  return Math.min(1, (node.r[2] * node.r[3]) / total);
}

/**
 * Compare two snapshots.
 *
 * Masked elements are dropped on both sides before anything else happens: the
 * user already told us that region is not part of the comparison, and honouring
 * that here is what keeps the text category usable on a page with a clock on it.
 */
export function diffDomSnapshots(
  baseline: DomSnapshot,
  actual: DomSnapshot,
  options: DomDiffOptions = {},
): DomDiffResult {
  const maxChanges = options.maxChanges ?? DEFAULT_MAX_CHANGES;
  const includeText = options.includeText !== false;
  const includeStyles = options.includeStyles !== false;

  const baseNodes = baseline.nodes.filter((node) => node.m !== 1);
  const actualNodes = actual.nodes.filter((node) => node.m !== 1);

  const { pairs, removed, added } = pairNodes(baseNodes, actualNodes);

  interface Scored {
    change: Omit<DomChange, 'rank'>;
    score: number;
  }
  const scored: Scored[] = [];

  for (const { before, after } of pairs) {
    const kinds = movedOrResized(before, after);
    const styles = includeStyles ? diffStyles(before, after) : [];
    if (styles.length > 0) kinds.push('restyled');

    const attrs = diffAttrs(before, after);
    if (attrs.length > 0) kinds.push('attrs');

    const textChanged = includeText && (before.x ?? '') !== (after.x ?? '');
    if (textChanged) kinds.push('text');

    if (kinds.length === 0) continue;

    const share = areaShare(after, actual.captureBox);
    scored.push({
      score: share,
      change: {
        path: after.p,
        label: labelOf(after),
        kinds,
        rect: after.r,
        baselineRect: before.r,
        areaPct: share,
        ...(attrs.length > 0 ? { attrs } : {}),
        ...(styles.length > 0 ? { styles } : {}),
        ...(textChanged ? { text: { from: before.x ?? '', to: after.x ?? '' } } : {}),
      },
    });
  }

  for (const node of added) {
    const share = areaShare(node, actual.captureBox);
    scored.push({
      score: share,
      change: {
        path: node.p,
        label: labelOf(node),
        kinds: ['added'],
        rect: node.r,
        areaPct: share,
      },
    });
  }

  for (const node of removed) {
    const share = areaShare(node, baseline.captureBox);
    scored.push({
      score: share,
      change: {
        path: node.p,
        label: labelOf(node),
        kinds: ['removed'],
        baselineRect: node.r,
        areaPct: share,
      },
    });
  }

  const rootCauses = scored.filter((entry) => isRootCause(entry.change.kinds)).length;

  // Root causes first, then movement, each by area. A reviewer wants the
  // element that was restyled, not the largest of the eleven things it shoved.
  //
  // When EVERYTHING only moved — a whole page nudged by a scrollbar, say —
  // nothing is demoted, because then the movement is the finding.
  scored.sort((a, b) => {
    if (rootCauses > 0) {
      const aRoot = isRootCause(a.change.kinds) ? 1 : 0;
      const bRoot = isRootCause(b.change.kinds) ? 1 : 0;
      if (aRoot !== bRoot) return bRoot - aRoot;
    }
    return b.score - a.score;
  });

  const changes: DomChange[] = scored.slice(0, maxChanges).map((entry, index) => ({
    rank: index + 1,
    ...entry.change,
    ...(rootCauses > 0 && !isRootCause(entry.change.kinds) ? { consequence: true } : {}),
  }));

  return { changes, total: scored.length, rootCauses };
}
